import type React from "react"
import { forwardRef } from "react"

import { cn } from "@/lib/utils"

interface ListUrgencyIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  criticalCount: number
  warningCount: number
}

export const ListUrgencyIndicator = forwardRef<
  HTMLDivElement,
  ListUrgencyIndicatorProps
>(({ criticalCount, warningCount, className, ...props }, ref) => {
  const isCritical = criticalCount > 0
  const isWarning = !isCritical && warningCount > 0
  const isStable = !isCritical && !isWarning

  return (
    <div
      ref={ref}
      className={cn(
        "flex h-4 w-4 shrink-0 cursor-grab items-center justify-center active:cursor-grabbing",
        className,
      )}
      aria-label={
        isCritical
          ? `${criticalCount} critical task${criticalCount !== 1 ? "s" : ""}`
          : isWarning
            ? `${warningCount} task${warningCount !== 1 ? "s" : ""} expiring soon`
            : "Drag to reorder"
      }
      {...props}
    >
      <span
        className={cn(
          "inline-block h-2 w-2 rounded-full transition-all",
          isCritical &&
            "bg-red-bright animate-pulse shadow-[0_0_6px_var(--red-glow)]",
          isWarning && "bg-yellow",
          isStable && "bg-bone-dim/30",
        )}
      />
    </div>
  )
})

ListUrgencyIndicator.displayName = "ListUrgencyIndicator"
